import { t } from '../../i18n';
import { CustomerDetail } from '../../types/customer';
import { InvoiceListItem } from '../../types/invoice';
import { formatDate, formatMoney } from '../../utils/format';
import {
  dateCell,
  downloadCustomerExcel,
  escapeHtml,
  excelHeaderRowNumber,
  excelSubtotal,
  htmlDataRow,
  htmlSummaryCard,
  moneyCell,
  printCustomerPdf,
} from './exportCustomerShared';

function invoiceHeaders(): string[] {
  return [
    t().invoices.invoiceDate,
    t().invoices.invoiceNo,
    t().invoices.dueDate,
    t().invoices.daysDue,
    t().invoices.category,
    t().financial.grandTotal,
    t().invoices.paid,
    t().invoices.outstanding,
    t().invoices.status,
  ];
}

function isNumeric(index: number): boolean {
  return index === 3 || index === 5 || index === 6 || index === 7;
}

function sumField(
  invoices: InvoiceListItem[],
  pick: (item: InvoiceListItem) => {
    invoice: string;
    cash: string;
    total: string;
  },
) {
  let invoice = 0;
  let cash = 0;
  let total = 0;
  for (const item of invoices) {
    const values = pick(item);
    invoice += Number(values.invoice);
    cash += Number(values.cash);
    total += Number(values.total);
  }
  return {
    invoice: invoice.toFixed(2),
    cash: cash.toFixed(2),
    total: total.toFixed(2),
  };
}

export function exportCustomerInvoicesExcel(
  customer: CustomerDetail,
  invoices: InvoiceListItem[],
): void {
  const labels = t().customers;
  const headers = invoiceHeaders();
  const charges = sumField(invoices, (item) => item.charges);
  const paid = sumField(invoices, (item) => item.paid);
  const outstanding = sumField(invoices, (item) => item.outstanding);
  const summaryRows = [
    [
      { type: 'empty' as const },
      { type: 'string' as const, value: t().financial.invoice, style: 'Section' },
      { type: 'string' as const, value: t().financial.cash, style: 'Section' },
      {
        type: 'string' as const,
        value: t().financial.grandTotal,
        style: 'Section',
      },
    ],
    [
      { type: 'string' as const, value: labels.totalCharges, style: 'Label' },
      moneyCell(charges.invoice),
      moneyCell(charges.cash),
      moneyCell(charges.total),
    ],
    [
      { type: 'string' as const, value: t().invoices.paid, style: 'Label' },
      moneyCell(paid.invoice),
      moneyCell(paid.cash),
      moneyCell(paid.total),
    ],
    [
      { type: 'string' as const, value: t().invoices.outstanding, style: 'Label' },
      moneyCell(outstanding.invoice, 'MoneyBold'),
      moneyCell(outstanding.cash, 'MoneyBold'),
      moneyCell(outstanding.total, 'MoneyBold'),
    ],
  ];
  const headerRow = excelHeaderRowNumber(summaryRows.length);
  const firstDataRow = headerRow + 1;
  const lastDataRow =
    invoices.length > 0 ? headerRow + invoices.length : headerRow;

  downloadCustomerExcel({
    customer,
    filename: `timologia-${customer.id}.xls`,
    sheetTitle: labels.tabs.invoices,
    columnWidths: [78, 90, 78, 55, 100, 82, 82, 82, 90],
    columnCount: 9,
    headers,
    isNumericHeader: isNumeric,
    summaryRows,
    dataRows: invoices.map((invoice) => [
      dateCell(invoice.invoiceDate),
      { type: 'string', value: invoice.invoiceNo },
      dateCell(invoice.dueDate),
      invoice.daysDue != null && invoice.daysDue > 0
        ? { type: 'number', value: invoice.daysDue, style: 'Int' }
        : { type: 'empty' },
      { type: 'string', value: invoice.category?.name || '—' },
      moneyCell(invoice.charges.total),
      moneyCell(invoice.paid.total),
      moneyCell(invoice.outstanding.total),
      { type: 'string', value: t().invoices.statuses[invoice.status] },
    ]),
    totalRow: [
      { type: 'string', value: t().common.total, style: 'TotalLabel' },
      { type: 'empty', style: 'TotalEmpty' },
      { type: 'empty', style: 'TotalEmpty' },
      { type: 'empty', style: 'TotalEmpty' },
      { type: 'empty', style: 'TotalEmpty' },
      excelSubtotal(6, firstDataRow, lastDataRow),
      excelSubtotal(7, firstDataRow, lastDataRow),
      excelSubtotal(8, firstDataRow, lastDataRow),
      { type: 'empty', style: 'TotalEmpty' },
    ],
  });
}

export function exportCustomerInvoicesPdf(
  customer: CustomerDetail,
  invoices: InvoiceListItem[],
): void {
  const labels = t().customers;
  const headers = invoiceHeaders();
  const charges = sumField(invoices, (item) => item.charges);
  const paid = sumField(invoices, (item) => item.paid);
  const outstanding = sumField(invoices, (item) => item.outstanding);

  printCustomerPdf({
    customer,
    title: labels.tabs.invoices,
    summaryHtml: `<div class="summary">${htmlSummaryCard(
      labels.totalCharges,
      charges.invoice,
      charges.cash,
      charges.total,
    )}${htmlSummaryCard(
      t().invoices.paid,
      paid.invoice,
      paid.cash,
      paid.total,
    )}${htmlSummaryCard(
      t().invoices.outstanding,
      outstanding.invoice,
      outstanding.cash,
      outstanding.total,
    )}</div>`,
    headers,
    isNumeric,
    items: invoices,
    rowHtml: (invoice) =>
      htmlDataRow(
        [
          formatDate(invoice.invoiceDate),
          invoice.invoiceNo,
          formatDate(invoice.dueDate),
          invoice.daysDue != null && invoice.daysDue > 0
            ? String(invoice.daysDue)
            : '—',
          invoice.category?.name || '—',
          formatMoney(invoice.charges.total),
          formatMoney(invoice.paid.total),
          formatMoney(invoice.outstanding.total),
          t().invoices.statuses[invoice.status],
        ],
        isNumeric,
      ),
    totalsRowHtml: `
      <tr>
        <td colspan="5">${escapeHtml(t().common.total)}</td>
        <td class="num">${escapeHtml(formatMoney(charges.total))}</td>
        <td class="num">${escapeHtml(formatMoney(paid.total))}</td>
        <td class="num">${escapeHtml(formatMoney(outstanding.total))}</td>
        <td></td>
      </tr>`,
  });
}
